import { useState } from 'react';
import { supabase } from '@/lib/supabase';

const CRON_SECRET = 'teknonovahub-cron-2026';
const FUNCTION_URL = 'https://ixduinqmfepnggmheoci.supabase.co/functions/v1/daily-news-cron';

const ALL_CATEGORIES = [
  { id: 'yapay-zeka', name: 'Yapay Zeka' },
  { id: 'mobil', name: 'Mobil' },
  { id: 'oyun', name: 'Oyun' },
  { id: 'donanim', name: 'Donanım' },
  { id: 'yazilim', name: 'Yazılım' },
  { id: 'girisimcilik', name: 'Girişimcilik' },
  { id: 'kripto', name: 'Kripto' },
  { id: 'bilim', name: 'Bilim' },
  { id: 'siber-guvenlik', name: 'Siber Güvenlik' },
  { id: 'otomotiv', name: 'Otomotiv' },
  { id: 'saglik', name: 'Sağlık' },
  { id: 'uzay', name: 'Uzay' },
  { id: 'fintech', name: 'Fintech' },
  { id: 'spor-teknolojisi', name: 'Spor Teknolojisi' },
  { id: 'egitim-teknolojisi', name: 'Eğitim Teknolojisi' },
  { id: 'cevre-enerji', name: 'Çevre & Enerji' },
  { id: 'drone-robot', name: 'Drone & Robot' },
  { id: 'metaverse-arvr', name: 'Metaverse & AR/VR' },
];

type RunResult = {
  success: boolean;
  total_inserted?: number;
  results?: { category: string; inserted: number }[];
  message?: string;
  timestamp?: string;
  error?: string;
};

type LogEntry = {
  id: string;
  time: string;
  status: 'success' | 'error' | 'running';
  message: string;
  details?: RunResult;
};

export default function CronTab() {
  const [selectedCats, setSelectedCats] = useState<string[]>(ALL_CATEGORIES.map(c => c.id));
  const [countPerCat, setCountPerCat] = useState(2);
  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);

  const addLog = (entry: Omit<LogEntry, 'id'>) => {
    const id = Date.now().toString();
    setLogs(prev => [{ ...entry, id }, ...prev].slice(0, 20));
    return id;
  };

  const updateLog = (id: string, updates: Partial<LogEntry>) => {
    setLogs(prev => prev.map(l => l.id === id ? { ...l, ...updates } : l));
  };

  const handleRunNow = async () => {
    if (selectedCats.length === 0) return;
    setRunning(true);
    const logId = addLog({
      time: new Date().toLocaleTimeString('tr-TR'),
      status: 'running',
      message: `${selectedCats.length} kategori için haber üretiliyor... (${countPerCat} haber/kategori)`,
    });

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || CRON_SECRET;

      const res = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          secret: CRON_SECRET,
          categories: selectedCats,
          count_per_category: countPerCat,
        }),
      });

      const result: RunResult = await res.json();

      if (result.success) {
        updateLog(logId, {
          status: 'success',
          message: `${result.total_inserted} haber eklendi (${selectedCats.length} kategori)`,
          details: result,
        });
      } else {
        updateLog(logId, {
          status: 'error',
          message: result.error || 'Bilinmeyen hata',
          details: result,
        });
      }
    } catch (err) {
      updateLog(logId, {
        status: 'error',
        message: String(err),
      });
    } finally {
      setRunning(false);
    }
  };

  const toggleCat = (id: string) => {
    setSelectedCats(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const selectAll = () => setSelectedCats(ALL_CATEGORIES.map(c => c.id));
  const selectNone = () => setSelectedCats([]);

  const inputCls = 'bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#0A0E27] focus:outline-none focus:border-[#0A0E27]';

  return (
    <div className="flex flex-col gap-6">
      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className="ri-information-line text-amber-600 text-lg" />
        </div>
        <div>
          <p className="text-sm font-semibold text-amber-800 mb-1">Otomatik Zamanlama Hakkında</p>
          <p className="text-xs text-amber-700 leading-relaxed">
            Her gün otomatik haber eklemek için Supabase Dashboard &rarr; Database &rarr; Extensions bölümünden{' '}
            <strong>pg_cron</strong> ve <strong>pg_net</strong> extension'larını etkinleştirin.
            Ardından aşağıdaki SQL kodunu çalıştırın. Şimdilik bu sayfadan manuel tetikleyebilirsiniz.
          </p>
        </div>
      </div>

      {/* SQL Setup Box */}
      <div className="bg-[#0A0E27] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-white/60 uppercase tracking-wider">Otomatik Zamanlama SQL (günde 1 kez, sabah 08:00)</p>
          <button
            onClick={() => {
              const sql = `SELECT cron.schedule(
  'daily-news-cron',
  '0 8 * * *',
  $$
  SELECT net.http_post(
    url := '${FUNCTION_URL}',
    headers := '{"Content-Type": "application/json", "Authorization": "Bearer ${CRON_SECRET}"}'::jsonb,
    body := '{"secret": "${CRON_SECRET}", "count_per_category": 2}'::jsonb
  );
  $$
);`;
              navigator.clipboard.writeText(sql);
            }}
            className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-clipboard-line" />Kopyala
          </button>
        </div>
        <pre className="text-xs text-[#00FF88] overflow-x-auto leading-relaxed">{`SELECT cron.schedule(
  'daily-news-cron',
  '0 8 * * *',
  $$
  SELECT net.http_post(
    url := '${FUNCTION_URL}',
    headers := '{"Content-Type":"application/json",
                 "Authorization":"Bearer ${CRON_SECRET}"}'::jsonb,
    body := '{"secret":"${CRON_SECRET}","count_per_category":2}'::jsonb
  );
  $$
);`}</pre>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Manual Run Panel */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-bold text-[#0A0E27] text-sm mb-4 flex items-center gap-2">
            <i className="ri-play-circle-line text-[#00FF88]" />
            Manuel Tetikleme
          </h3>

          {/* Count Per Category */}
          <div className="mb-4">
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              Kategori başına haber sayısı
            </label>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 5].map(n => (
                <button
                  key={n}
                  onClick={() => setCountPerCat(n)}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-bold border transition-colors cursor-pointer ${
                    countPerCat === n
                      ? 'bg-[#0A0E27] text-white border-[#0A0E27]'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-[#0A0E27]'
                  }`}
                >
                  {n}
                </button>
              ))}
              <input
                type="number"
                min={1}
                max={10}
                value={countPerCat}
                onChange={e => setCountPerCat(Math.max(1, Math.min(10, Number(e.target.value))))}
                className={`${inputCls} w-16 text-center`}
              />
            </div>
          </div>

          {/* Category Selection */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-500">Kategoriler</label>
              <div className="flex gap-2">
                <button onClick={selectAll} className="text-xs text-[#0A0E27] hover:underline cursor-pointer">Tümü</button>
                <span className="text-gray-300">|</span>
                <button onClick={selectNone} className="text-xs text-gray-400 hover:underline cursor-pointer">Hiçbiri</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-1 max-h-52 overflow-y-auto pr-1">
              {ALL_CATEGORIES.map(cat => (
                <label
                  key={cat.id}
                  className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer hover:text-[#0A0E27] select-none py-1"
                >
                  <input
                    type="checkbox"
                    checked={selectedCats.includes(cat.id)}
                    onChange={() => toggleCat(cat.id)}
                    className="w-3.5 h-3.5 rounded border-gray-300 text-[#0A0E27] focus:ring-[#0A0E27] cursor-pointer"
                  />
                  {cat.name}
                </label>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg px-3 py-2 mb-4 text-xs text-gray-500">
            Toplam <strong className="text-[#0A0E27]">{selectedCats.length * countPerCat}</strong> haber eklenecek
            ({selectedCats.length} kategori × {countPerCat})
          </div>

          <button
            onClick={handleRunNow}
            disabled={running || selectedCats.length === 0}
            className="w-full flex items-center justify-center gap-2 bg-[#0A0E27] text-white rounded-lg py-2.5 text-sm font-bold hover:bg-[#0A0E27]/80 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {running ? (
              <><i className="ri-loader-4-line animate-spin" />Haberler ekleniyor...</>
            ) : (
              <><i className="ri-play-fill" />Şimdi Çalıştır</>
            )}
          </button>
        </div>

        {/* Run Logs */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-bold text-[#0A0E27] text-sm mb-4 flex items-center gap-2">
            <i className="ri-history-line text-gray-400" />
            Çalıştırma Geçmişi
            {logs.length > 0 && (
              <span className="ml-auto text-xs font-normal text-gray-400">{logs.length} kayıt</span>
            )}
          </h3>

          {logs.length === 0 ? (
            <div className="text-center py-12 text-gray-300">
              <i className="ri-timer-line text-3xl block mb-2" />
              <p className="text-xs">Henüz çalıştırılmadı</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2 max-h-80 overflow-y-auto pr-1">
              {logs.map(log => (
                <div key={log.id} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      {log.status === 'running' && <i className="ri-loader-4-line text-blue-500 animate-spin text-sm" />}
                      {log.status === 'success' && <i className="ri-checkbox-circle-line text-green-500 text-sm" />}
                      {log.status === 'error' && <i className="ri-error-warning-line text-red-500 text-sm" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-semibold text-[#0A0E27]">{log.time}</span>
                        {log.status === 'success' && (
                          <span className="text-[10px] bg-green-100 text-green-700 font-bold px-1.5 py-0.5 rounded-full">Başarılı</span>
                        )}
                        {log.status === 'error' && (
                          <span className="text-[10px] bg-red-100 text-red-600 font-bold px-1.5 py-0.5 rounded-full">Hata</span>
                        )}
                        {log.status === 'running' && (
                          <span className="text-[10px] bg-blue-100 text-blue-600 font-bold px-1.5 py-0.5 rounded-full">Çalışıyor</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">{log.message}</p>
                      {log.details?.results && (
                        <button
                          onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                          className="text-xs text-[#0A0E27] hover:underline cursor-pointer mt-1"
                        >
                          {expandedLog === log.id ? 'Gizle' : 'Detaylar'} <i className={expandedLog === log.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
                        </button>
                      )}
                      {expandedLog === log.id && log.details?.results && (
                        <div className="mt-2 grid grid-cols-2 gap-1">
                          {log.details.results.map(r => (
                            <div key={r.category} className="flex items-center justify-between text-[10px] bg-gray-50 rounded px-2 py-1">
                              <span className="text-gray-500">{r.category}</span>
                              <span className="font-bold text-[#0A0E27]">+{r.inserted}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
